import { useCallback, useEffect, useMemo, useRef } from 'react';
import { createRealtimeServiceFromAngularWebSocket, toOptionalNumber } from '../../../services/Utilities';
import AssignedRealtimeService from './AssignedRealtimeService';

interface RealtimeEventDetail {
  detail?: {
    record?: unknown;
  };
}

export interface ExperienceWithIdAndDate {
  id: number;
  startDate: Date;
}

interface AssignedRealtimeOptions<TExperience extends ExperienceWithIdAndDate> {
  loadActiveExperiences: (shouldUpdate: () => boolean) => Promise<void> | void;
  scheduledExperiences: TExperience[];
  endedExperiences: TExperience[];
  setActiveExperiences: React.Dispatch<React.SetStateAction<TExperience[]>>;
  setScheduledExperiences: React.Dispatch<React.SetStateAction<TExperience[]>>;
  setEndedExperiences: React.Dispatch<React.SetStateAction<TExperience[]>>;
  mapSingleExperienceToCard: (experience: unknown) => TExperience | null;
  insertExperienceByStartDate: (experiences: TExperience[], newExperience: TExperience) => TExperience[];
}

export function mapSingleExperienceToCard<TExperience>(
  experience: unknown,
  mapExperiencesToCards: (experiences: unknown[]) => TExperience[]
): TExperience | null {
  try {
    return mapExperiencesToCards([experience])[0] ?? null;
  } catch {
    return null;
  }
}

export function insertExperienceByStartDate<TExperience extends ExperienceWithIdAndDate>(
  experiences: TExperience[],
  newExperience: TExperience
): TExperience[] {
  const withoutExisting = experiences.filter((experience) => experience.id !== newExperience.id);
  if (withoutExisting.length === 0) {
    return [newExperience];
  }

  const isAscendingByStartDate = withoutExisting.length < 2
    ? true
    : withoutExisting[0].startDate.getTime() <= withoutExisting[withoutExisting.length - 1].startDate.getTime();

  return [...withoutExisting, newExperience].sort((a, b) =>
    isAscendingByStartDate
      ? a.startDate.getTime() - b.startDate.getTime()
      : b.startDate.getTime() - a.startDate.getTime()
  );
}

export function useAssignedRealtime<TExperience extends ExperienceWithIdAndDate>({
  loadActiveExperiences,
  scheduledExperiences,
  endedExperiences,
  setActiveExperiences,
  setScheduledExperiences,
  setEndedExperiences,
  mapSingleExperienceToCard,
  insertExperienceByStartDate,
}: AssignedRealtimeOptions<TExperience>): void {
  const scheduledExperiencesRef = useRef<TExperience[]>(scheduledExperiences);
  const endedExperiencesRef = useRef<TExperience[]>(endedExperiences);

  useEffect(() => {
    scheduledExperiencesRef.current = scheduledExperiences;
  }, [scheduledExperiences]);

  useEffect(() => {
    endedExperiencesRef.current = endedExperiences;
  }, [endedExperiences]);

  const assignedRealtimeService = useMemo(() => {
    return createRealtimeServiceFromAngularWebSocket<AssignedRealtimeService>(AssignedRealtimeService);
  }, []);

  const handleNewExperienceNotification = useCallback((e: Event) => {
    const detail = (e as Event & RealtimeEventDetail).detail;
    const experience = detail && detail.record;
    if (!experience) {
      return;
    }
    void loadActiveExperiences(() => true);
  }, [loadActiveExperiences]);

  const handleUpdateExperienceNotification = useCallback((e: Event) => {
    const detail = (e as Event & RealtimeEventDetail).detail;
    const experienceRecord = detail && detail.record;
    if (!experienceRecord) {
      return;
    }

    const experienceData = experienceRecord as Record<string, unknown>;
    const experienceId = toOptionalNumber(experienceData.id);
    const experienceStatus = String(experienceData.status ?? '').toLowerCase();
    if (!experienceId) {
      return;
    }

    if (experienceStatus === 'archived') {
      setActiveExperiences((previous) => previous.filter((experience) => experience.id !== experienceId));
      setScheduledExperiences((previous) => previous.filter((experience) => experience.id !== experienceId));
      setEndedExperiences((previous) => previous.filter((experience) => experience.id !== experienceId));
      return;
    }

    if (experienceStatus !== 'active') {
      void loadActiveExperiences(() => true);
      return;
    }

    const scheduledMatch = scheduledExperiencesRef.current.find((experience) => experience.id === experienceId);
    const endedMatch = endedExperiencesRef.current.find((experience) => experience.id === experienceId);
    const matchingExperience = scheduledMatch || endedMatch;

    if (!matchingExperience) {
      void loadActiveExperiences(() => true);
      return;
    }

    const updatedExperience = mapSingleExperienceToCard(experienceRecord) ?? matchingExperience;

    setScheduledExperiences((previous) => previous.filter((experience) => experience.id !== experienceId));
    setEndedExperiences((previous) => previous.filter((experience) => experience.id !== experienceId));
    setActiveExperiences((previous) => insertExperienceByStartDate(previous, updatedExperience));
  }, [
    insertExperienceByStartDate,
    loadActiveExperiences,
    mapSingleExperienceToCard,
    setActiveExperiences,
    setEndedExperiences,
    setScheduledExperiences,
  ]);

  useEffect(() => {
    if (!assignedRealtimeService) {
      return;
    }

    assignedRealtimeService.on(
      AssignedRealtimeService.EVENTS.XPNewExperienceNotification,
      handleNewExperienceNotification
    );
    assignedRealtimeService.on(
      AssignedRealtimeService.EVENTS.XPUpdateExperienceNotification,
      handleUpdateExperienceNotification
    );

    return () => {
      assignedRealtimeService.removeListener(
        AssignedRealtimeService.EVENTS.XPNewExperienceNotification,
        handleNewExperienceNotification
      );
      assignedRealtimeService.removeListener(
        AssignedRealtimeService.EVENTS.XPUpdateExperienceNotification,
        handleUpdateExperienceNotification
      );
    };
  }, [assignedRealtimeService, handleNewExperienceNotification, handleUpdateExperienceNotification]);
}

export default useAssignedRealtime;
